import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ShieldCheck } from "lucide-react";

const occasions = [
  { key: "all", label: "All Work" },
  { key: "births", label: "Births" },
  { key: "memorials", label: "Memorials" },
  { key: "awards", label: "Awards" },
  { key: "business", label: "Business" },
  { key: "gifts", label: "Gifts" },
  { key: "kids", label: "Kids & Learning" },
  { key: "stickers", label: "Stickers & Prints" },
  { key: "firearms", label: "Firearms" },
  { key: "art", label: "Art" },
];

const memorialSubs = [
  { key: "all", label: "All Memorials" },
  { key: "human", label: "Human" },
  { key: "pet", label: "Pet" },
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sub, setSub] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter !== "all") params.occasion = filter;
    if (filter === "memorials" && sub !== "all") params.subcategory = sub;
    api.get("/gallery", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [filter, sub]);

  const setPrimary = (k) => { setFilter(k); setSub("all"); };

  return (
    <div data-testid="gallery-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Idea Library</div>
      <h1 className="font-serif text-5xl sm:text-6xl mt-3 max-w-3xl leading-none" style={{color: "var(--primary)"}}>A catalog of possibilities.</h1>
      <p className="mt-5 max-w-xl text-[color:var(--text-muted)] leading-relaxed">Browse past commissions organized by occasion and material. Use any as a starting point in the Design Studio.</p>

      <div data-testid="gallery-filters" className="mt-10 flex flex-wrap gap-2">
        {occasions.map((o) => (
          <Button key={o.key} data-testid={`filter-${o.key}`} onClick={() => setPrimary(o.key)}
            variant={filter === o.key ? "default" : "outline"}
            className={filter === o.key ? "rounded-full btn-primary" : "rounded-full"}>{o.label}</Button>
        ))}
      </div>

      {filter === "memorials" && (
        <div data-testid="memorial-subfilters" className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)] mr-1">For</span>
          {memorialSubs.map((s) => (
            <button key={s.key} data-testid={`subfilter-${s.key}`} onClick={() => setSub(s.key)}
              className={`text-xs px-4 py-1.5 rounded-full border transition ${sub === s.key ? "bg-[color:var(--secondary)] text-white border-transparent" : "hairline text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"}`}>{s.label}</button>
          ))}
        </div>
      )}

      {filter === "firearms" && (
        <div data-testid="firearms-compliance-note" className="mt-6 rounded-lg border hairline p-5 flex items-start gap-3" style={{background: "rgba(197,160,89,0.08)"}}>
          <ShieldCheck size={18} strokeWidth={1.5} style={{color: "var(--accent-metal)"}} className="mt-0.5 flex-shrink-0"/>
          <div className="text-sm text-[color:var(--text-main)] leading-relaxed">
            <span className="font-semibold" style={{color: "var(--primary)"}}>Compliance note:</span> We never engrave, alter, or obscure firearm serial numbers under any circumstance. Our work is limited to monograms, scrollwork, decorative text, and custom imagery on slides, grips, lowers and other non-regulated surfaces.
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-16 text-center text-[color:var(--text-muted)]">Loading library…</div>
      ) : items.length === 0 ? (
        <div className="mt-16 text-center text-[color:var(--text-muted)]">No pieces yet in this category — check back soon.</div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:auto-rows-[320px]">
          {items.map((it, i) => {
            const cls = i % 5 === 0 ? "md:col-span-8 md:row-span-2" : "md:col-span-4";
            return (
              <div key={it.id} data-testid={`gallery-item-${it.id}`} className={`${cls} group flex flex-col overflow-hidden rounded-lg border hairline card-lift bg-white`}>
                <div className="relative flex-1 min-h-0 overflow-hidden bg-[color:var(--muted)] aspect-[4/3] md:aspect-auto">
                  <img src={it.image} alt={it.title} loading="lazy" className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"/>
                </div>
                <div className="p-5 border-t hairline">
                  <div className="uppercase text-[10px] tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>{it.material} · {it.occasion}{it.subcategory ? ` · ${it.subcategory}` : ""}</div>
                  <div className="font-serif text-xl mt-1" style={{color: "var(--primary)"}}>{it.title}</div>
                  <div className="text-xs text-[color:var(--text-muted)] mt-1 leading-relaxed">{it.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
